'use client';
import React from 'react';
import classNames from 'classnames';

import styled, { useTheme } from '@benzinga/themetron';
import { Mover } from '@benzinga/movers-manager';
import { getColorByValue } from '@benzinga/frontend-utils';
import { formatLarge, formatPercentage, formatPrice } from '@benzinga/utils/numberUtils';
import { Ticker } from '@benzinga/ticker-ui';
import { ErrorBoundary } from '@benzinga/core-ui';
import { HeaderSortIcons, SortBy } from './HeaderSortIcons';

export type StockMoversLayoutType = 'compact' | 'default' | 'nano';

export interface StockMoversTableProps {
  adPlacement?: React.ReactElement;
  layout?: StockMoversLayoutType;
  isLoading?: boolean;
  movers: Mover[];
  session?: string;
  hideTickerPopup?: boolean;
  options?: {
    hideColumns?: {
      averageVolume?: boolean;
      companyName?: boolean;
    };
  };
  isEdgeSubscriber?: boolean;
}

const sortMovers = (movers: Mover[], sortBy: SortBy) => {
  const sortedMovers = [...movers];

  sortedMovers.sort((a, b) => {
    if (a[sortBy.field] === b[sortBy.field]) return 1;
    if (!a[sortBy.field]) return 1;
    if (!b[sortBy.field]) return -1;

    if (a[sortBy.field] && b[sortBy.field] && Number(a[sortBy.field]) < Number(b[sortBy.field])) {
      return sortBy.direction === 'asc' ? -1 : 1;
    }
    if (b[sortBy.field] && b[sortBy.field] && Number(a[sortBy.field]) > Number(b[sortBy.field])) {
      return sortBy.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  return sortedMovers;
};

export const StockMoversTable: React.FC<StockMoversTableProps> = ({
  adPlacement,
  hideTickerPopup = false,
  isEdgeSubscriber = false,
  isLoading = false,
  movers = [],
  options,
  session,
}) => {
  const theme = useTheme();
  const [sortBy, setSortBy] = React.useState<SortBy>({});

  const handleClickSortByHeader = React.useCallback(
    (newSortByField: string) => {
      let direction: 'desc' | 'asc' = 'asc';
      if (sortBy?.direction === 'asc') {
        direction = 'desc';
        setSortBy({ direction, field: newSortByField });
      } else if (sortBy?.direction === 'desc') {
        setSortBy({});
      } else {
        setSortBy({ direction, field: newSortByField });
      }
    },
    [sortBy],
  );

  const sortedMovers = sortMovers(movers, sortBy);

  const showCompanyName = !options?.hideColumns || (options?.hideColumns && !options?.hideColumns?.companyName);
  const showAverageVolume = !options?.hideColumns || (options?.hideColumns && !options?.hideColumns?.averageVolume);

  return (
    <Container $isEdgeSubscriber={isEdgeSubscriber} className="stock-movers-table">
      <div className="stock-movers-table-wrapper overflow-x-auto">
        <table>
          <thead className="whitespace-nowrap">
            <tr>
              <th className="ticker" colSpan={1}>
                Ticker
              </th>
              {showCompanyName && <th colSpan={2}>Company</th>}
              <th className="close" colSpan={1}>
                {isEdgeSubscriber ? (
                  <button className="w-full" onClick={() => handleClickSortByHeader('close')}>
                    {session ? 'Close' : 'Price'}
                    <HeaderSortIcons field="close" sortBy={sortBy} />
                  </button>
                ) : (
                  <span className="w-full">{session ? 'Close' : 'Price'}</span>
                )}
              </th>
              <th className="change-percent">±%</th>
              {showAverageVolume && (
                <>
                  <th className="average-volume" colSpan={1}>
                    {isEdgeSubscriber ? (
                      <button onClick={() => handleClickSortByHeader('averageVolume')}>
                        Avg. Vol
                        <HeaderSortIcons field="averageVolume" sortBy={sortBy} />
                      </button>
                    ) : (
                      <span>Avg. Vol</span>
                    )}
                  </th>
                  {/* <th className="market-cap" colSpan={1}>
                    <button onClick={() => handleClickSortByHeader('marketCap')}>
                      Mkt Cap
                      <HeaderSortIcons field="marketCap" sortBy={sortBy} />
                    </button>
                  </th> */}
                </>
              )}
              <th className="buy-stock-table-header w-[60px] text-right" colSpan={1}>
                Buy Stock
              </th>
            </tr>
          </thead>
          <tbody className={classNames({ 'no-results': !sortedMovers?.length })}>
            {Array.isArray(sortedMovers) &&
              sortedMovers.length > 0 &&
              sortedMovers.slice(0, 12).map((mover, index) => {
                const tableRow = (
                  <tr className={!isEdgeSubscriber && index >= 4 ? 'blur-row' : ''} key={`${mover.symbol}-${index}`}>
                    <td className="ticker" colSpan={1} title={mover.symbol}>
                      <Ticker
                        hideChange={true}
                        hidePopup={hideTickerPopup}
                        symbol={mover.symbol}
                        targetElement={
                          <a href={`https://www.benzinga.com/quote/${mover.symbol}`} rel="noreferrer" target="_blank">
                            {mover.symbol}
                          </a>
                        }
                      />
                    </td>
                    {showCompanyName && (
                      <td className="company" colSpan={2} title={mover.companyName}>
                        <b>
                          <a href={`https://www.benzinga.com/quote/${mover.symbol}`} rel="noreferrer" target="_blank">
                            {mover.companyName}
                          </a>
                        </b>
                      </td>
                    )}
                    <td className="close" colSpan={1} title={mover.close?.toString()}>
                      {`$${formatPrice(mover.close, { fixedPlace: 2, showNearZero: false })}` || '—'}
                    </td>
                    <td
                      className="change"
                      colSpan={1}
                      style={{ color: getColorByValue(theme, mover.changePercent) }}
                      title={Number.isFinite(mover.changePercent) ? `${mover.changePercent}%` : '—'}
                    >
                      {Number.isFinite(mover.changePercent) ? `${formatPercentage(mover.changePercent)}%` : '—'}
                    </td>
                    {showAverageVolume && (
                      <>
                        <td className="average-volume" colSpan={1} title={mover?.averageVolume?.toString()}>
                          {mover.averageVolume ? formatLarge(mover.averageVolume) : '—'}
                        </td>
                        {/* <td className="market-cap" colSpan={1} title={mover?.marketCap?.toString()}>
                        {mover.marketCap ? `$${formatLarge(Number(mover.marketCap))}` : '—'}
                      </td> */}
                      </>
                    )}
                    <td className="buy" colSpan={1} title="Buy/Sell" width={60}>
                      <a href="https://www.benzinga.com/go/start-investing/" rel="nofollow sponsored">
                        Buy/Sell
                      </a>
                    </td>
                  </tr>
                );

                return (
                  <React.Fragment>
                    {tableRow}
                    {adPlacement && sortedMovers.length > 3 && index === 2 && (
                      <ErrorBoundary name="stock-movers-table-ad-placement">
                        <tr>
                          <td>{adPlacement}</td>
                        </tr>
                      </ErrorBoundary>
                    )}
                  </React.Fragment>
                );
              })}
            {!Array.isArray(sortedMovers) ||
              (Array.isArray(sortedMovers) && !sortedMovers.length && (
                <tr>
                  <td>
                    <div className="h-full w-full text-center p-4">{isLoading ? 'Loading...' : 'No Results'}</div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <a
        className="benzinga-pro-link"
        data-label="Get Real-time Movers"
        href="https://www.benzinga.com/go/benzinga-pro/"
      >
        Get in Real-time
      </a>
    </Container>
  );
};

export default StockMoversTable;

// TODO: UPDATE PADDING
// http://local.benzinga.com:3000/money/everything-you-need-to-know-about-apple-stock

const Container = styled.div<{ $isEdgeSubscriber: boolean }>`
  &.stock-movers-table {
    width: 100%;

    .stock-movers-table-wrapper {
      display: block;

      table {
        width: 100%;
        table-layout: fixed;
        display: table;
        text-align: left;
        font-size: ${({ theme }) => theme.fontSize.sm};
        border: 1px solid #e1ebfa;
      }

      thead {
        display: table;
        table-layout: fixed;
        width: 100%;
        color: #5b7292;
        border-bottom: 2px solid ${({ theme }) => theme.colors.border};
        font-size: ${({ theme }) => theme.fontSize.xs};
        background-color: #f2f8fe;
        height: 35px;

        tr {
          text-align: left;
          border-collapse: collapse;

          @media screen and (max-width: 800px) {
            height: 30px;
          }

          th:last-of-type {
            text-align: right;
          }
        }

        th {
          padding: 0 0.25rem;

          &.change-percent {
            width: 5rem;
            max-width: 5rem;
          }

          &:last-of-type {
            text-align: right;
            padding-left: unset;
          }

          button {
            display: flex;
            align-items: center;
            justify-content: space-between;
            font-weight: ${({ theme }) => theme.fontWeight.semibold};
          }
        }

        .header-sort-icons {
          display: flex;
          flex-direction: column;
        }

        .header-sort-icon {
          width: 8px;
          height: 6px;
          font-size: 6px;
          margin-left: 4px;
          color: ${({ theme }) => theme.colorPalette.gray200};

          &__up,
          &__down {
            &.active {
              color: ${({ theme }) => theme.colorPalette.gray500};
            }
          }
        }
      }

      tbody {
        max-height: 260px;
        min-height: 80px;
        overflow-x: hidden;
        display: block;
        width: 100%;
        font-size: 0.75rem;
        position: relative;

        &::-webkit-scrollbar {
          display: none;
        }

        th,
        td {
          padding: 0.2rem 0.25rem;
        }

        tr {
          border-collapse: collapse;
          box-sizing: inherit;
          display: table;
          table-layout: fixed;
          width: 100%;
          border-bottom: 1px solid #f0f0f0;

          @media screen and (max-width: 950px) {
            height: 38px;
          }

          &:hover {
            background-color: #dfeefd;
          }

          &.blur-row {
            filter: blur(4px);
            pointer-events: none;

            &:hover {
              background-color: transparent;
            }
          }

          td {
            color: ${({ theme }) => theme.colorPalette.black};

            &.company {
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              color: #182940;
              a {
                color: #283d59;

                &:hover {
                  color: #3f83f8;
                }
              }
            }

            &.close {
              color: #182940;
              font-weight: ${({ theme }) => theme.fontWeight.semibold};
            }

            &.average-volume {
              color: #182940;
              font-weight: ${({ theme }) => theme.fontWeight.semibold};
            }

            &.change {
              width: 5rem;
              max-width: 5rem;
              min-width: 0;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              font-weight: ${({ theme }) => theme.fontWeight.semibold};
            }

            a {
              color: #2ca2d1;
            }

            &.buy {
              text-align: right;
              font-weight: ${({ theme }) => theme.fontWeight.semibold};
            }

            &.ticker {
              font-weight: ${({ theme }) => theme.fontWeight.semibold};
              a {
                color: #283d59;

                &:hover {
                  color: #3f83f8;
                }
              }
            }
          }
        }

        &.no-results {
          tr {
            border-bottom: unset;
          }
        }
      }
    }

    .benzinga-pro-link {
      display: flex;
      justify-content: space-between;
      padding-bottom: 5px;
      padding-top: 5px;
      font-size: 10px;
      color: #2ca2d1;
      font-weight: 700;
      width: 100%;
      border-top: 1px solid ${({ theme }) => theme.colors.border};

      @media screen and (max-width: 800px) {
        line-height: 26px;
        font-size: 12px;
      }
    }
  }
`;
